import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import config from "./config";
import "./assets/scss/style.scss";
import { store, persister } from "./store";

/**
 * startApp
 *
 * @summary Initializes and renders the root React application with Redux, persistence, and routing.
 *
 * @description
 * Creates the React root and renders the App component wrapped with Redux Provider, PersistGate for state persistence, and BrowserRouter for routing. Passes the newVersionAvailable flag to the App for version management.
 *
 * @param {boolean} [newVersionAvailable=false] - Indicates if a new app version is available.
 *
 * @returns {void}
 *
 * @example
 * startApp();
 * startApp(true);
 */
function startApp(newVersionAvailable = false) {
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <Provider store={store}>
      <PersistGate loading={null} persistor={persister}>
        <BrowserRouter basename={config.basename}>
          <App newVersionAvailable={newVersionAvailable} />
        </BrowserRouter>
      </PersistGate>
    </Provider>
  );
}

fetch(process.env.PUBLIC_URL + "/meta.json?time=" + Date.now(), { cache: "no-store" })
  .then((res) => res.json())
  .then((data) => {
    const currentVersion = data.version;
    const storedVersion = localStorage.getItem("app_version");

    if (storedVersion && storedVersion !== currentVersion) {
      startApp(true);
    } else {
      localStorage.setItem("app_version", currentVersion);
      startApp(false);
    }
  })
  .catch((err) => {
    console.warn("Version check failed:", err);
    startApp(false);
  });

/**
 * setInterval (version polling)
 *
 * @summary Periodically checks for a new app version by polling the meta.json file.
 *
 * @description
 * Every 30 seconds, fetches the meta.json file with a cache-busting timestamp. Compares the fetched version with the version stored in localStorage. If a new version is detected, sets a flag in localStorage to indicate a new version is available.
 *
 * @returns {void}
 *
 * @example
 * setInterval(() => {
 *   // version polling logic
 * }, 30000);
 */
setInterval(() => {
  fetch(process.env.PUBLIC_URL + "/meta.json?time=" + Date.now(), { cache: "no-store" })
    .then((res) => res.json())
    .then((data) => {
      const storedVersion = localStorage.getItem("app_version");
      if (storedVersion && storedVersion !== data.version) {
        localStorage.setItem("new_version_detected", "true");
      }
    })
    .catch(() => {});
}, 30000);